import { HttpClient } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { Injectable } from '@angular/core';
import { filter, firstValueFrom, map, Subject, take, takeUntil } from 'rxjs';
import { MapHelperService } from 'src/services/map-helper.service';
import { FeatureCollection, Polygon } from '@turf/helpers';
import { environment } from 'src/environments/environment';
import { HierarchyLevel, SelectionHistoryItem } from '../models/polygon-selector-with-levels.models';
import { LayerAPIService } from 'src/app/features/shared/services/API/LayersAPI.service';

@Injectable()
export class PolygonSelectorWithLevelsService {
  
  isSelectionActive = true;
  hoveredPolygonId: any = null;
  hoveredPolygonLevelIndex: any = null;
  
  // GeoJSONs guardados por nivel
  geoJsonsSaved: FeatureCollection<Polygon>[] = [];
  
  //control de jerarquia
  currentHierarchyIndex: number = 0;
  selectionHistory: SelectionHistoryItem[] = [];
  private hierarchyLevels: HierarchyLevel[] = [];

  // prefijos 
  private sourcePrefix = 'polygon-selector-source-';
  private layerPrefix = 'layer-polygon-selector-';
  private layerLinePrefix = 'layer-line-polygon-selector-';
  private layerTextPrefix = 'layer-polygon-selector-text-';

  private levelLineColors: string[] = ['#1329f0', '#51bbd6', '#f1f075', '#223b53', '#e55e5e'];
  private levelFillColors: string[] = ['#a0c8f0', '#90e0f0', '#f9f4a5', '#627e90', '#f28b82'];
  
  private onSelectionChangeCallback: ((history: SelectionHistoryItem[], selectedFeature?: GeoJSON.Feature) => void)  = null;

  // Flag para saber si ya se inicializó
  private isInitialized: boolean = false;

  private eventHandlers: Map<string, { click: any, mousemove: any, mouseleave: any }> = new Map();

  private destroy$ = new Subject<void>();

  constructor(
    private mapHelperService: MapHelperService,
    private http: HttpClient,
    private layersAPIService: LayerAPIService
  ) {}

  configure( hierarchyLevels: HierarchyLevel[],  sourceAdd?: string ): void {
    this.hierarchyLevels = hierarchyLevels;
    
    if (sourceAdd) {
      this.sourcePrefix = this.sourcePrefix + sourceAdd + '-';
      this.layerPrefix = this.layerPrefix + sourceAdd + '-';
      this.layerLinePrefix = this.layerLinePrefix + sourceAdd + '-';
      this.layerTextPrefix = this.layerTextPrefix + sourceAdd + '-';
    }

    // Asignar nombres de source y layers a cada nivel si no vienen seteados
    this.hierarchyLevels.forEach((level, index) => {
      if (!level.sourceName) {
        level.sourceName = this.getSourceName(index);
      }
      if (!level.layerName) {
        level.layerName = this.getLayerName(index);
      }
      if (!level.layerLineName) {
        level.layerLineName = this.getLayerLineName(index);
      }
      if (!level.layerTextName) {
        level.layerTextName = this.getLayerTextName(index);
      }
    });
  }

  async initialize(skipLayerCreation: boolean = false) {
    if (this.hierarchyLevels.length === 0) {
      console.log('selector, no hay niveles de jerarquía configurados');
      return;
    }

    if (this.mapHelperService._map && this.mapHelperService.isMainSourceReady) {
      if (!skipLayerCreation) {
        this.createAllLevels();
      } else {
        this.setupEventHandlers();
      }
      return;
    }

    try {
      await firstValueFrom(
        this.mapHelperService.mainSource$.pipe(
          filter(state => state != null && state.isMainSourceReady),
          take(1)
        )
      );
      if (!skipLayerCreation) {
        this.createAllLevels();
      } else {
        this.setupEventHandlers();
      }
    } catch (e) {
      console.error('selector, Error esperando al mapa', e);
    }
  }

  private createAllLevels(): void {
    if (this.isInitialized) return;

    this.hierarchyLevels.forEach((level, index) => {
      this.createSourceAndLayersForLevel(index);
    });

    this.isInitialized = true;
  }

  // configurar event handlers cuando las capas ya fueron creadas externamente
  private setupEventHandlers(): void {
    if (this.isInitialized) return;

    this.hierarchyLevels.forEach((level, index) => {
      const layerName = level.layerName;
      
      const clickHandler = (e: any) => this.onLayerClick(e, index);
      const mousemoveHandler = (e: any) => this.onLayerHover(e, index);
      const mouseleaveHandler = (e: any) => this.onLayerUnhover(e, index);
      
      this.eventHandlers.set(layerName, {
        click: clickHandler,
        mousemove: mousemoveHandler,
        mouseleave: mouseleaveHandler
      });

      this.mapHelperService._map.on('click', layerName, clickHandler);
      this.mapHelperService._map.on('mousemove', layerName, mousemoveHandler);
      this.mapHelperService._map.on('mouseleave', layerName, mouseleaveHandler);
    });

    this.isInitialized = true;
    console.log('selector con niveles: event handlers configurados para capas existentes');
  }

  //este va a crear el evento para notificar cambios y activa lña funcion del componente
  private notifySelectionChange(selectedFeature?: GeoJSON.Feature): void {
    if (this.onSelectionChangeCallback) {
      this.onSelectionChangeCallback([...this.selectionHistory], selectedFeature);
    }
  }

  //este se configura al inicio del componente y tiene la funcionque va a hacer algo con los cambios
  onSelectionChange(callback: (history: SelectionHistoryItem[], selectedFeature?: GeoJSON.Feature) => void): void {
    this.onSelectionChangeCallback = callback;
  }

  private createSourceAndLayersForLevel(levelIndex: number): void {
    const level = this.hierarchyLevels[levelIndex];
    const sourceName = level.sourceName;
    const layerName = level.layerName;
    const layerLineName = level.layerLineName;
    const layerTextName = level.layerTextName;

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);
    
    // se elvaron un nivel porque no las reconocia el off
    const clickHandler = (e: any) => this.onLayerClick(e, levelIndex);
    const mousemoveHandler = (e: any) => this.onLayerHover(e, levelIndex);
    const mouseleaveHandler = (e: any) => this.onLayerUnhover(e, levelIndex);
    
    this.eventHandlers.set(layerName, {
      click: clickHandler,
      mousemove: mousemoveHandler,
      mouseleave: mouseleaveHandler
    });

    if (!existingSource) {
      const color = this.getColorForLevel(levelIndex, this.levelFillColors);
      const lineColor = this.getColorForLevel(levelIndex, this.levelLineColors);

      this.mapHelperService.AddSourceEmptyGeoJson(sourceName, "Polígonos con niveles nivel " + levelIndex, 'annotation-polygon-selector.png');
      this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Polígonos con niveles, nivel " + levelIndex, color);
      this.mapHelperService.AddLayerLine(layerLineName, sourceName, "Líneas de polígonos con niveles, nivel " + levelIndex, lineColor, 3);
      this.mapHelperService.AddLayerText(layerTextName, sourceName, 'Description', "Etiqueta de polígonos con niveles, nivel " + levelIndex, -1);
    } else {
      if (!environment.production) console.log("selector, source ya existe para nivel", levelIndex);
    }
    this.mapHelperService._map.on('click', layerName, clickHandler);
    this.mapHelperService._map.on('mousemove', layerName, mousemoveHandler);
    this.mapHelperService._map.on('mouseleave', layerName, mouseleaveHandler);
  }

  // #region eventos de las capas por nivel
  private onLayerHover(e: any, levelIndex: number): void {
    if (!this.isSelectionActive || !e.features?.length) return;
    if (levelIndex !== this.currentHierarchyIndex) return;

    const feature = e.features[0];
    const sourceName = this.hierarchyLevels[levelIndex].sourceName;

    // limpia el hover anterior si existe
    if (this.hoveredPolygonId != null && this.hoveredPolygonLevelIndex != null) {
      const previousSourceName = this.hierarchyLevels[this.hoveredPolygonLevelIndex].sourceName;
      this.mapHelperService._map.setFeatureState(
        { source: previousSourceName, id: this.hoveredPolygonId },
        { hover: false }
      );
    }

    this.hoveredPolygonId = feature.id;
    this.hoveredPolygonLevelIndex = levelIndex;

    this.mapHelperService._map.getCanvas().style.cursor = 'pointer';
    this.mapHelperService._map.setFeatureState( { source: sourceName, id: this.hoveredPolygonId }, { hover: true } );
  }

  private onLayerUnhover(e: any, levelIndex: number): void {
    if (!this.isSelectionActive) return;
    if (this.hoveredPolygonId == null || this.hoveredPolygonLevelIndex == null) return;

    const sourceName = this.hierarchyLevels[this.hoveredPolygonLevelIndex].sourceName;
    this.mapHelperService._map.getCanvas().style.cursor = '';
    this.mapHelperService._map.setFeatureState( { source: sourceName, id: this.hoveredPolygonId }, { hover: false } ); 
    this.hoveredPolygonId = null;
    this.hoveredPolygonLevelIndex = null;
  }

  private async onLayerClick(e: any, levelIndex: number): Promise<void> {
    if (!this.isSelectionActive || !e.features?.length) return;
    if (levelIndex !== this.currentHierarchyIndex) return;

    const feature = e.features[0];
    const currentLevel = this.hierarchyLevels[levelIndex];
    const sourceName = currentLevel.sourceName;
    const selectedId = feature.properties[currentLevel.idProperty];

    // para evitar spam de un nivel
    const lastSelection = this.selectionHistory[this.selectionHistory.length - 1];
    if (lastSelection?.levelIndex === levelIndex && lastSelection?.selectedId === selectedId) return;

    const isSelected = this.mapHelperService._map.getFeatureState({ source: sourceName, id: feature.id }).selected;

    if (!isSelected) {
      this.deselectAllForLevel(levelIndex);

      if (!environment.production) {
        console.log("selector, click en nivel", levelIndex, feature);
      }

      this.selectionHistory.push({
        levelIndex,
        selectedId,
        description: feature.properties['Description'] || selectedId
      });

      this.notifySelectionChange(feature);

      //checa el nivel
      if (levelIndex < this.hierarchyLevels.length - 2) {
        await this.loadAndShowNextLevel(selectedId);
      } else {
        // no hace nada al final, desdee l penultimo
        if (!environment.production) {
          console.log("selector, último nivel alcanzado");
        }
      }
    } else {
      this.mapHelperService._map.setFeatureState({ source: sourceName, id: feature.id }, { selected: false });
    }
  }
  // #endregion

  private async loadAndShowNextLevel(parentId: string): Promise<void> {
    const nextLevelIndex = this.currentHierarchyIndex + 1;
    const nextLevel = this.hierarchyLevels[nextLevelIndex];

    if (!nextLevel) return;

    try {
      let geoJson: FeatureCollection<Polygon>;

      // cargar geoJson si no está en caché
      if (this.geoJsonsSaved[nextLevelIndex]) {
        geoJson = this.geoJsonsSaved[nextLevelIndex];
      } else {
        // geoJson = await this.loadGeoJsonDummy(nextLevel.path);
        geoJson = await this.loadGeoJson(nextLevel.key, parentId);
        if (!geoJson) 
          return;
        this.geoJsonsSaved[nextLevelIndex] = geoJson;
      }

      // Filtrar por el padre seleccionado
      // const filteredFeatures = geoJson.features.filter( feature => feature.properties && feature.properties[nextLevel.parentIdProperty!] == parentId );

      // if (filteredFeatures.length === 0) {
      //   if (!environment.production) {
      //     console.log(`selector, sin features de hijo`, nextLevel.key,parentId);
      //   }
      //   return;
      // }

      // const filteredGeoJson: FeatureCollection<Polygon> = {
      //   type: 'FeatureCollection',
      //   features: filteredFeatures
      // };

      const nextSourceName = nextLevel.sourceName;
      this.mapHelperService.UpdateLayerGeoJson(nextSourceName, geoJson);
      this.showLayersForLevel(nextLevelIndex);
      this.currentHierarchyIndex = nextLevelIndex;
      this.mapHelperService.FitBounds(geoJson);

    } catch (e) {
      console.error('selector, error cargando nivel', nextLevel, e);
    }
  }

  private getColorForLevel(levelIndex: number, colors: string[]): string {
    const colorArray = colors.length > 0 ? colors : this.levelFillColors;
    return colorArray[levelIndex % colorArray.length];
  }

  // #region Nombres de sources y layers
  getSourceName(levelIndex: number): string {
    return this.sourcePrefix + levelIndex;
  }

  getLayerName(levelIndex: number): string {
    return this.layerPrefix + levelIndex;
  }

  getLayerLineName(levelIndex: number): string {
    return this.layerLinePrefix + levelIndex;
  }

  getLayerTextName(levelIndex: number): string {
    return this.layerTextPrefix + levelIndex;
  }
  // #endregion

  // #region Visibilidad de layers
  hideLayersForLevel(levelIndex: number): void {
    const level = this.hierarchyLevels[levelIndex];
    this.mapHelperService.HideLayer(level.layerName);
    this.mapHelperService.HideLayer(level.layerLineName);
    this.mapHelperService.HideLayer(level.layerTextName);
  }

  showLayersForLevel(levelIndex: number): void {
    const level = this.hierarchyLevels[levelIndex];
    this.mapHelperService.ShowLayer(level.layerName);
    this.mapHelperService.ShowLayer(level.layerLineName);
    this.mapHelperService.ShowLayer(level.layerTextName);
  }
  // #endregion

  deselectAllForLevel(levelIndex: number): void {
    const level = this.hierarchyLevels[levelIndex];
    const sourceName = level.sourceName;
    const layerName = level.layerName;

    if (!this.mapHelperService._map.getLayer(layerName)) 
      return;

    const features = this.mapHelperService.getLayerFeatures(layerName);
    features.forEach((feature) => {
      this.mapHelperService._map.setFeatureState({ source: sourceName, id: feature.id }, { selected: false });
    });
  }



  async goToPreviousLevel(): Promise<void> {
    if (this.currentHierarchyIndex === 0) 
      return;
    
    this.selectionHistory.pop();
    this.geoJsonsSaved.pop();
      
    const previousLevelIndex = this.currentHierarchyIndex - 1;
    const previousLevel = this.hierarchyLevels[previousLevelIndex];
      
    try {
      const currentLevel = this.hierarchyLevels[this.currentHierarchyIndex];
      const currentSourceName = currentLevel.sourceName;
      this.hideLayersForLevel(this.currentHierarchyIndex);
      this.mapHelperService.UpdateLayerGeoJson(currentSourceName, { type: 'FeatureCollection', features: [] });
      
      if (previousLevelIndex === 0) {
        this.showLayersForLevel(previousLevelIndex);
          
        const previousSourceName = previousLevel.sourceName;
        if (this.mapHelperService.isSourceEmpty(previousSourceName)) {
          let geoJson = this.geoJsonsSaved[previousLevelIndex];
          if (!geoJson && previousLevel.path) {
            // geoJson = await this.loadGeoJsonDummy(previousLevel.path);
            geoJson = await this.loadGeoJson(previousLevel.key, '');
            this.geoJsonsSaved[previousLevelIndex] = geoJson;
          }
          if (geoJson) {
            this.mapHelperService.UpdateLayerGeoJson(previousSourceName, geoJson);
          }
        }
      } else {
        const parentSelection = this.selectionHistory[this.selectionHistory.length - 1];
        const parentId = parentSelection?.selectedId;
        
        let geoJson = this.geoJsonsSaved[previousLevelIndex];
        if (!geoJson && previousLevel.path) {
          // geoJson = await this.loadGeoJsonDummy(previousLevel.path);
          geoJson = await this.loadGeoJson(previousLevel.key, parentId);
          this.geoJsonsSaved[previousLevelIndex] = geoJson;
        }

        if (parentId && previousLevel.parentIdProperty && geoJson) {
          const filteredFeatures = geoJson.features.filter( feature => feature.properties && feature.properties[previousLevel.parentIdProperty!] == parentId );
          const filteredGeoJson: FeatureCollection<Polygon> = { 
            type: 'FeatureCollection', 
            features: filteredFeatures 
          };
          
          this.mapHelperService.UpdateLayerGeoJson(previousLevel.sourceName, filteredGeoJson);
        }
          
        this.showLayersForLevel(previousLevelIndex);
      }
        
      this.deselectAllForLevel(previousLevelIndex);
      this.currentHierarchyIndex = previousLevelIndex;
        
      const source = this.mapHelperService.GetSourceByName(previousLevel.sourceName);
      if (source && (source as any)._data) {
        this.mapHelperService.FitBounds((source as any)._data);
      }
      
      this.notifySelectionChange();
        
    } catch (e) {
      this.mapHelperService.FlyToCamera(
        this.mapHelperService.Lat.toString(),
        this.mapHelperService.Lng.toString(),
        this.mapHelperService.Zoom.toString(),
        0
      );
      console.error(`Error regresando al nivel`,previousLevel.key, e);
    }
  }

  async resetToInitialLevel(resetAll: boolean): Promise<void> {
    var resetToLevel = 1;
    if(resetAll){
      resetToLevel = 0;
    }
    if (this.hierarchyLevels.length === 0) return;

    const initialLevel = this.hierarchyLevels[0];
    
    try {
      // limpia todos los niveles excepto el inicial
      for (let i = this.currentHierarchyIndex; i >= resetToLevel; i--) {
        const level = this.hierarchyLevels[i];
        const sourceName = level.sourceName;
        this.deselectAllForLevel(i);
        this.mapHelperService.UpdateLayerGeoJson(sourceName, this.mapHelperService.Empty_GeoJson);
        this.hideLayersForLevel(i);
        this.geoJsonsSaved[i] = null as any;
      }

      const sourceName = initialLevel.sourceName;
      let geoJson = this.geoJsonsSaved[0];
      
      if (!geoJson && initialLevel.path) {
        // geoJson = await this.loadGeoJsonDummy(initialLevel.path);
        geoJson = await this.loadGeoJson(initialLevel.key, '');
        this.geoJsonsSaved[0] = geoJson;
      }

      if (geoJson) {
        this.deselectAllForLevel(0);
        this.mapHelperService.UpdateLayerGeoJson(sourceName, geoJson);
        this.showLayersForLevel(0);
      }
      
      this.selectionHistory = [];
      this.currentHierarchyIndex = 0;
      this.hoveredPolygonLevelIndex = 0;
      this.geoJsonsSaved = this.geoJsonsSaved.slice(0, 1);
      
      this.notifySelectionChange();
        
    } catch (e) {
      console.error('selector, error reseteando al nivel inicial', e);
    }
  }

  async loadInitialLevel(): Promise<void> {
    if (this.hierarchyLevels.length === 0) {
      console.log('selector, no hay niveles de jerarquía configurados');
      return;
    }

    this.currentHierarchyIndex = 0;
    this.selectionHistory = [];
      
    try {
      const initialLevel = this.hierarchyLevels[0];
      const sourceName = initialLevel.sourceName;
      
      if (initialLevel.key) {
        // const geoJson = await this.loadGeoJsonDummy(initialLevel.path);
        const geoJson = await this.loadGeoJson(initialLevel.key, '');
        if (!geoJson) return;
        this.mapHelperService.UpdateLayerGeoJson(sourceName, geoJson);
        this.geoJsonsSaved[0] = geoJson;
        this.showLayersForLevel(0);
        this.mapHelperService.FitBounds(geoJson);
      }
      
      this.notifySelectionChange();
    } catch (e) {
      this.mapHelperService.FlyToCamera(
        this.mapHelperService.Lat.toString(),
        this.mapHelperService.Lng.toString(),
        this.mapHelperService.Zoom.toString(),
        0
      );
      console.error('selector, error cargando nivel inicial', e);
    }
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.eventHandlers.forEach((handlers, layerName) => {
      if (this.mapHelperService._map) {
        this.mapHelperService._map.off('click', layerName, handlers.click);
        this.mapHelperService._map.off('mousemove', layerName, handlers.mousemove);
        this.mapHelperService._map.off('mouseleave', layerName, handlers.mouseleave);
      }
    });
    this.eventHandlers.clear();

    for (let i = 0; i <= this.currentHierarchyIndex; i++) {
        this.deselectAllForLevel(i);
        this.hideLayersForLevel(i);
    }

    this.geoJsonsSaved = [];
    this.currentHierarchyIndex = 0;
    this.selectionHistory = [];
    this.onSelectionChangeCallback = null;
    this.isInitialized = false;
  }

  setSelectionActive(active: boolean): void {
    this.isSelectionActive = active;
  }

  async loadGeoJson(zoneName: string, selectedZoneId: string): Promise<FeatureCollection<Polygon> | null> {
      const request = this.layersAPIService.GetLayerDataFilteredByNameGeojson(zoneName, selectedZoneId)
        .pipe( takeUntil(this.destroy$), map((geoResp) => {
            console.log("selector, loadGeoJson layer", geoResp);
  
            try {
              if (!geoResp?.isSuccess) {
                console.log("selector, no hubo respuesta del servidor");
                Sentry.captureMessage('PolygonSelectorWithLevelsService.loadGeoJson: isSuccess false', { level: 'warning', extra: { response: geoResp } });
                return null;
              }
  
              const dataArray = JSON.parse(geoResp.result) as { [key: string]: any }[];
              const features: GeoJSON.Feature<Polygon>[] = [];
  
              dataArray.forEach((item, index) => {
                if (item.GeomJson) {
                  try {
                    const geometry = JSON.parse(item.GeomJson);
                    const properties = { ...item };
                    delete properties['GeomJson'];
  
                    const feature: GeoJSON.Feature<Polygon> = {
                      type: 'Feature',
                      id: index + 1,
                      geometry: geometry,
                      properties: properties
                    };
  
                    features.push(feature);
                  } catch (parseError) {
                    console.log(`selector, error parseando el diccionario`, parseError);
                  }
                }
              });
  
              const geoJson: FeatureCollection<Polygon> = {
                type: 'FeatureCollection',
                features: features
              };
  
              if (!environment.production) console.log('selector, GeoJSON cargado', geoJson);
  
              return geoJson;
            } catch (error) {
              console.log("selector, error parseando el geojson", error);
              return null;
            }
          })
        );
  
      try {
        return await firstValueFrom(request);
      } catch (error: any) {
        if (error?.name === 'EmptyError') {
          return null;
        }
        console.error('selector, error cargando GeoJSON', error);
        throw error;
      }
    }
  async loadGeoJsonDummy(path: string): Promise<FeatureCollection<Polygon>> {
    const geoJson = await firstValueFrom(this.http.get(path)) as FeatureCollection<Polygon>;
    
    if (!environment.production) console.log('selector, GeoJSON cargado', geoJson);
      
    geoJson.features.forEach((feature, index) => {
      if (feature.id == undefined || feature.id == null) {
        feature.id = index + 1;
      }
    });
    
    return geoJson;
  }

  getSelectionHistory(): SelectionHistoryItem[] {
    return [...this.selectionHistory];
  }

  getCurrentLevelIndex(): number {
    return this.currentHierarchyIndex;
  }

  getHierarchyLevels(): HierarchyLevel[] {
    return [...this.hierarchyLevels];
  }

  restoreFullGeoJsonForCurrentLevel(): void {
    const currentLevel = this.hierarchyLevels[this.currentHierarchyIndex];
    if (!currentLevel) return;

    const geoJson = this.geoJsonsSaved[this.currentHierarchyIndex];
    if (geoJson) {
      this.mapHelperService.UpdateLayerGeoJson(currentLevel.sourceName, geoJson);
      this.deselectAllForLevel(this.currentHierarchyIndex);
      if (!environment.production) {
        console.log('selector, GeoJSON completo restaurado para nivel', this.currentHierarchyIndex);
      }
    }
  }
}
